import { z, modelCreator, isObject, isTrulyValue, safeJsonParse, isEmpty, type DeepPartial, objectSize, DeepObjectPlainMerge, generateUUID } from "@app/@turnkeyidutils"

export const mintyLoggerLevel = z.enum([`silent`, `trace`, `debug`, `info`, `warn`, `error`, `fatal`])
export type MintyLoggerLevel = z.infer<typeof mintyLoggerLevel>
export const getMintyLoggerLevel = mintyLoggerLevel.parse

export class MintyLoggerOptions {
  constructor(

    /** @default void 0 => auto generated */
    public id: string,

    /** @default true */
    public logFileEnabled: boolean,

    /** @default {{process.CWD}}/storage/logs */
    public logPath: string,

    /** @default 'pretty_log'' */
    public logFilePrefix: string,

    /** @default 4 */
    public minDumpSizeKB: number,

    /** @default 16 */
    public maxDumpSizeKB: number,

    /** @default 8 */
    public maxDebugDepth: number,

    /** @default { consoleMin: 'info', dumpMax: 'debug', syncLogger: true } */
    public logLevelOptions: {
      consoleMin?: MintyLoggerLevel
      dumpMax?: MintyLoggerLevel
      syncLogger?: boolean
    },

    /** @default undefined */
    public allowedKeywords?: string[],

    /** @default none */
    public debugging: `none` | `debug` | `verbose` = `none`,

    /** @default ['password','pass','pin','token'] */
    public bannedKeywords: string[] = [`password`, `pass`, `pin`, `token`],

  ) {}

  static create = modelCreator(MintyLoggerOptions)
}

export type MintyLoggerDataAndOptions = {
  debug?: unknown
  message?: string
  __options?: Partial<MintyLoggerOptions>
}

export type MintyLoggerResult = {
  printedMessage: string
  level: string
  optionsAndData: MintyLoggerDataAndOptions
  moduleNameOrLabel: string
  debugMessage?: string
  debugging: {
    printConsole: boolean
    isTooBig: boolean
    isDumped: boolean
    debugObject?: Record<any, any>
  }
}

export type MintyLoggerType = {
  <O extends MintyLoggerDataAndOptions = MintyLoggerDataAndOptions>(label: string, options?: O, level?: MintyLoggerLevel): MintyLoggerResult | undefined
  <DO>(label: string, debugOrOptions?: DO, level?: MintyLoggerLevel): MintyLoggerResult | undefined
  <DO>(label: string, debugOrOptions?: DO, level?: string): MintyLoggerResult | undefined
}

const truncateText = (
  txt: string,
  options?: {
    maxSizeKB?: number
    maxLength?: number
  }) => {
  const { maxLength, maxSizeKB } = { maxSizeKB: 16, maxLength: 64, ...options }
  if (isEmpty(txt))
    return ``

  if (typeof txt !== `string`)
    // !TODO: using safeJsonStringify is resource intensive, need to benchmark first
    // txt = safeJsonStringify(txt)
    txt = String(txt)

  if (objectSize(txt, `KB`) > maxSizeKB)
    return `${ txt.slice(0, maxLength) }...<TRUNCATED_TEXT>`

  return txt
}

/**
 * Minty Logger, child of Pretty Logger
 * This don't save any logs file, and dont format the logs.
 * It's just have the save structure as Pretty Logger and maybe has the same options.
 * @param moduleNameOrLabel
 * @param optionsAndData
 * @param level
 * @returns
 */
export const MintyLogger: MintyLoggerType = (moduleNameOrLabel, optionsAndData, level = `info`) => {
  const getTime = () => new Date().toISOString()
  const validLevel = getMintyLoggerLevel(level.toLowerCase())

  let message: string | undefined = void 0
  let immutableOptionOrData
    = isObject(optionsAndData) && !isTrulyValue(optionsAndData)
      ? { ...safeJsonParse(optionsAndData) }
      : (
        !isEmpty(optionsAndData)
          ? (message = String(optionsAndData))
          : void 0
      )
  // Avoiding changing mutable data

  let currentOptions = isObject(immutableOptionOrData)
    ? ({ ...immutableOptionOrData })
    : void 0

  const { message: debugMessage, __options, error, ...debug }: { [key: string]: any; __options: DeepPartial<MintyLoggerOptions> } = currentOptions ?? ({} as any)
  debugMessage && (message = `${ debugMessage }`)
  const loggerOptions = __options

  const mainMessage = `[${ truncateText(moduleNameOrLabel, { maxSizeKB: loggerOptions.maxDumpSizeKB }) }]${ message ? ` ${ truncateText(message) }` : `` }`

  const printedMessage = `[${ getTime() }] ${ validLevel.toUpperCase() } (${ process.pid } on MAYBE): ${ mainMessage }`

  // GC
  currentOptions = void 0
  immutableOptionOrData = void 0

  return {
    printedMessage,
    level: validLevel,
    optionsAndData,
    moduleNameOrLabel,
    debugMessage,
    debugging: Object.assign({}, {
      printConsole: true,
      isTooBig: false,
      isDumped: false,
      debugObject: {},
    }),
  }
}

export type MintyLoggerFactoryType = (
  label: string,
  options?: DeepPartial<MintyLoggerOptions>,
) => {
  log: MintyLoggerType
  error: MintyLoggerType
  child: (
    childLabel: string,
    childOptions?: DeepPartial<MintyLoggerOptions>,
  ) => ReturnType<MintyLoggerFactoryType>
  getOptions: () => MintyLoggerOptions
}

export const MintyLoggerFactory: MintyLoggerFactoryType = (
  label: string,
  options?: DeepPartial<MintyLoggerOptions>,
) => {
  const mergedOptions = DeepObjectPlainMerge({ id: generateUUID() }, options ?? {})

  const log: MintyLoggerType = (...arguments_) => MintyLogger(
    `${ label }:${ arguments_[0] }`,
    isTrulyValue(arguments_[1])
      ? {
        debug: arguments_[1],
        __options: mergedOptions,
      }
      : DeepObjectPlainMerge({ __options: mergedOptions }, arguments_[1]),
    arguments_[2],
  )
  const error: MintyLoggerType = (...arguments_) => MintyLogger(
    `${ label }:${ arguments_[0] }`,
    isTrulyValue(arguments_[1])
      ? {
        debug: arguments_[1],
        __options: mergedOptions,
      }
      : DeepObjectPlainMerge({ __options: mergedOptions }, arguments_[1]),
    arguments_[2] ?? `error`,
  )

  return {
    log,
    error,
    child: (
      childLabel: string,
      childOptions?: DeepPartial<MintyLoggerOptions>,
    ) => MintyLoggerFactory(
      `${ label }:${ childLabel }`,
      DeepObjectPlainMerge(mergedOptions, childOptions ?? {}),
    ),
    getOptions: () => mergedOptions as MintyLoggerOptions,
  }
}
